import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { addDays, differenceInDays, isBefore } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { usePendingActions, useUnresolvedComments } from "@/hooks/use-dashboard";
import { useAuth, type UserRole } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { PendingAction, UnresolvedComment } from "@/lib/dashboard-data";
import { formatFramework, useActiveProgramHealth } from "@/hooks/use-compliance-programs";

type PolicyStatus = "draft" | "under_review" | "pending_approval" | "approved" | "rejected";

interface PolicyRow {
  id: string;
  title: string;
  status: PolicyStatus;
  next_renewal_date: string | null;
  created_at: string;
  created_by: string | null;
}

interface DashboardSummary {
  totalPolicies: number;
  approvedPolicies: number;
  draftPolicies: number;
  underReviewPolicies: number;
  pendingApprovalPolicies: number;
  renewalDue: number;
  teamMembers: number;
  recentWorkflowActivity: number;
  ownedDrafts: number;
  complianceRate: number;
}

const emptySummary: DashboardSummary = {
  totalPolicies: 0,
  approvedPolicies: 0,
  draftPolicies: 0,
  underReviewPolicies: 0,
  pendingApprovalPolicies: 0,
  renewalDue: 0,
  teamMembers: 0,
  recentWorkflowActivity: 0,
  ownedDrafts: 0,
  complianceRate: 0,
};

const roleLabels: Record<UserRole, string> = {
  admin: "Admin",
  creator: "Creator",
  reviewer: "Reviewer",
  approver: "Approver",
  viewer: "Viewer",
};

function getPrimaryRole(roles: UserRole[]): UserRole {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("approver")) return "approver";
  if (roles.includes("reviewer")) return "reviewer";
  if (roles.includes("creator")) return "creator";
  return "viewer";
}

function getRoleIntro(role: UserRole) {
  switch (role) {
    case "admin":
      return "Your operational view across workflow health, renewal risk, and team activity.";
    case "creator":
      return "Your drafting queue, follow-ups, and policies that need to move forward.";
    case "reviewer":
      return "Your review queue, unresolved comments, and policies waiting on feedback.";
    case "approver":
      return "Your approval queue, decision-ready policies, and upcoming renewal risk.";
    default:
      return "Approved policies, recent movement, and items worth checking next.";
  }
}

function getActionCta(status: PendingAction["status"]) {
  switch (status) {
    case "draft":
      return "Continue";
    case "under_review":
      return "Review";
    case "pending_approval":
      return "Approve";
    case "approved":
      return "Open";
    default:
      return "Open";
  }
}

function getActionMeta(action: PendingAction) {
  if (action.status === "approved") {
    if (action.subtitle) return action.subtitle;
    if (typeof action.expires_in_days === "number") {
      return action.expires_in_days < 0
        ? `Expired ${Math.abs(action.expires_in_days)} days ago`
        : `Renews in ${action.expires_in_days} days`;
    }
    return "Renewal date needs attention";
  }

  if (action.status === "draft" && action.created_at) {
    const days = differenceInDays(new Date(), new Date(action.created_at));
    if (days === 0) return "Created today";
    if (days === 1) return "Created yesterday";
    return `Drafted ${days} days ago`;
  }

  if (action.status === "under_review") {
    const count = action.assigned_reviewers?.length ?? 0;
    return count === 1 ? "1 reviewer assigned" : `${count} reviewers assigned`;
  }

  if (action.status === "pending_approval") {
    const count = action.assigned_approvers?.length ?? 0;
    return count === 1 ? "1 approver assigned" : `${count} approvers assigned`;
  }

  return action.status.replace("_", " ");
}

function getActionTone(status: PendingAction["status"]) {
  switch (status) {
    case "approved":
      return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/20 dark:text-orange-300";
    case "pending_approval":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300";
    case "under_review":
      return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/20 dark:text-blue-300";
    default:
      return "border-muted bg-muted/50 text-muted-foreground";
  }
}

function useDashboardSummary() {
  const { profile } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary>(emptySummary);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.company_id) return;

    let cancelled = false;

    async function fetchSummary() {
      setLoading(true);
      const companyId = profile.company_id;
      const now = new Date();
      const thirtyDaysFromNow = addDays(now, 30);
      const sevenDaysAgo = addDays(now, -7);

      try {
        const [{ data: policiesData }, { data: teamData }, { data: workflowData }] = await Promise.all([
          supabase
            .from("policies")
            .select("id,title,status,next_renewal_date,created_at,created_by")
            .eq("company_id", companyId),
          supabase.from("profiles").select("id").eq("company_id", companyId),
          supabase
            .from("policy_workflow")
            .select("id,created_at")
            .eq("company_id", companyId)
            .gte("created_at", sevenDaysAgo.toISOString()),
        ]);

        if (cancelled) return;

        const policies = (policiesData ?? []) as PolicyRow[];
        const approvedPolicies = policies.filter((policy) => policy.status === "approved").length;
        const totalPolicies = policies.length;
        const renewalDue = policies.filter((policy) => {
          if (!policy.next_renewal_date || policy.status !== "approved") return false;
          return isBefore(new Date(policy.next_renewal_date), thirtyDaysFromNow);
        }).length;

        setSummary({
          totalPolicies,
          approvedPolicies,
          draftPolicies: policies.filter((policy) => policy.status === "draft").length,
          underReviewPolicies: policies.filter((policy) => policy.status === "under_review").length,
          pendingApprovalPolicies: policies.filter((policy) => policy.status === "pending_approval").length,
          renewalDue,
          teamMembers: teamData?.length ?? 0,
          recentWorkflowActivity: workflowData?.length ?? 0,
          ownedDrafts: policies.filter((policy) => policy.status === "draft" && policy.created_by === profile.id).length,
          complianceRate: totalPolicies > 0 ? Math.round((approvedPolicies / totalPolicies) * 100) : 0,
        });
      } catch (error) {
        console.error("Error fetching tailored dashboard summary:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchSummary();

    return () => {
      cancelled = true;
    };
  }, [profile?.company_id, profile?.id]);

  return { summary, loading };
}

function RoleSummaryCards({
  role,
  summary,
  actions,
  comments,
  loading,
}: {
  role: UserRole;
  summary: DashboardSummary;
  actions: PendingAction[];
  comments: UnresolvedComment[];
  loading: boolean;
}) {
  const reviewActions = actions.filter((action) => action.status === "under_review").length;
  const approvalActions = actions.filter((action) => action.status === "pending_approval").length;
  const renewalActions = actions.filter((action) => action.status === "approved").length;

  const cards = {
    admin: [
      { label: "Workflow Queue", value: summary.underReviewPolicies + summary.pendingApprovalPolicies, hint: "Review and approval", icon: Clock },
      { label: "Renewal Risk", value: summary.renewalDue, hint: "Due or due soon", icon: RefreshCw },
      { label: "Team Activity", value: summary.recentWorkflowActivity, hint: "Last 7 days", icon: TrendingUp },
    ],
    creator: [
      { label: "Drafts To Finish", value: summary.ownedDrafts, hint: "Owned by you", icon: FileText },
      { label: "Follow-Ups", value: comments.length, hint: "Open comment threads", icon: MessageSquare },
      { label: "Renewal Watch", value: renewalActions, hint: "Your stakeholder policies", icon: RefreshCw },
    ],
    reviewer: [
      { label: "Reviews Waiting", value: reviewActions, hint: "Assigned to you", icon: ShieldCheck },
      { label: "Open Comments", value: comments.length, hint: "Need resolution", icon: MessageSquare },
      { label: "Renewal Watch", value: renewalActions, hint: "Stakeholder policies", icon: RefreshCw },
    ],
    approver: [
      { label: "Approvals Waiting", value: approvalActions, hint: "Assigned to you", icon: CheckCircle },
      { label: "Open Comments", value: comments.length, hint: "Before final sign-off", icon: MessageSquare },
      { label: "Renewal Risk", value: renewalActions, hint: "Needs visibility", icon: RefreshCw },
    ],
    viewer: [
      { label: "Approved Policies", value: summary.approvedPolicies, hint: "Available to read", icon: CheckCircle },
      { label: "Recent Movement", value: summary.recentWorkflowActivity, hint: "Last 7 days", icon: TrendingUp },
      { label: "Renewal Watch", value: summary.renewalDue, hint: "Due or due soon", icon: RefreshCw },
    ],
  } satisfies Record<UserRole, Array<{ label: string; value: number; hint: string; icon: typeof Clock }>>;

  if (loading) {
    return (
      <div className="grid gap-3 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <Skeleton className="mb-3 h-4 w-24" />
              <Skeleton className="mb-2 h-8 w-14" />
              <Skeleton className="h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-3">
      {cards[role].map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.label} className="overflow-hidden">
            <CardContent className="flex items-start justify-between p-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                <div className="mt-2 text-3xl font-medium tabular-nums text-foreground">{card.value}</div>
                <p className="mt-1 text-xs text-muted-foreground">{card.hint}</p>
              </div>
              <div className="rounded-md bg-muted p-2 text-muted-foreground">
                <Icon className="h-4 w-4" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ProgramHealthCard() {
  const navigate = useNavigate();
  const { program, health, loading } = useActiveProgramHealth();

  if (loading) {
    return (
      <Card>
        <CardContent className="space-y-4 p-5">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-3 w-full" />
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!program || !health) return null;

  return (
    <Card className="border-primary/20 bg-primary/[0.03]">
      <CardContent className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">Program Health</Badge>
              <Badge variant="outline">{formatFramework(program.framework)}</Badge>
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">{program.name}</h2>
              <p className="text-sm text-muted-foreground">
                {health.openTasks} open tasks · {health.metControls}/{health.totalControls} controls met · {health.satisfiedEvidence}/{health.totalEvidence} evidence items ready
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Readiness</span>
                <span className="font-medium">{health.readinessPercent}%</span>
              </div>
              <Progress value={health.readinessPercent} />
            </div>
          </div>
          <Button onClick={() => navigate(`/compliance/programs/${program.id}`)}>
            <Target className="mr-2 h-4 w-4" />
            Open program
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkItem({ action }: { action: PendingAction }) {
  const navigate = useNavigate();
  const href = action.status === "draft" ? `/policy-creator?edit=${action.id}` : `/policy/${action.id}`;

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">{action.title}</p>
          <Badge variant="outline" className={`shrink-0 text-[11px] ${getActionTone(action.status)}`}>
            {action.status === "pending_approval" ? "Approval" : action.status.replace("_", " ")}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{getActionMeta(action)}</p>
      </div>
      <Button size="sm" className="shrink-0" onClick={() => navigate(href)}>
        {getActionCta(action.status)}
      </Button>
    </li>
  );
}

function CommentItem({ comment }: { comment: UnresolvedComment }) {
  const navigate = useNavigate();

  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{comment.policy_title}</p>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{comment.content}</p>
      </div>
      <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(`/policy/${comment.policy_id}`)}>
        Open
      </Button>
    </li>
  );
}

function SectionEmpty({ role, title }: { role: UserRole; title: string }) {
  const navigate = useNavigate();
  const viewer = role === "viewer";

  return (
    <div className="rounded-lg border border-dashed bg-muted/20 p-4">
      <p className="text-sm font-medium text-foreground">Nothing waiting here.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {viewer
          ? "Browse approved policies or check recent updates when you need context."
          : `${title} is clear right now. Use this space to spot the next item that needs your input.`}
      </p>
      {viewer && (
        <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate("/policy-library")}>
          Browse Policies
        </Button>
      )}
    </div>
  );
}

function WorkSection({
  title,
  description,
  count,
  children,
  empty,
}: {
  title: string;
  description: string;
  count: number;
  children: ReactNode;
  empty: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold text-foreground">{title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {count}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>{count > 0 ? children : empty}</CardContent>
    </Card>
  );
}

function YourWork({
  role,
  actions,
  comments,
  loading,
}: {
  role: UserRole;
  actions: PendingAction[];
  comments: UnresolvedComment[];
  loading: boolean;
}) {
  const reviewActions = actions.filter((action) => action.status === "under_review");
  const approvalActions = actions.filter((action) => action.status === "pending_approval");
  const draftActions = actions.filter((action) => action.status === "draft");
  const renewalActions = actions.filter((action) => action.status === "approved");

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardContent className="space-y-3 p-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(role === "reviewer" || role === "admin") && (
        <WorkSection
          title="Needs Your Review"
          description="Policies assigned to you for feedback before they can move forward."
          count={reviewActions.length}
          empty={<SectionEmpty role={role} title="Reviews" />}
        >
          <ul className="space-y-2">{reviewActions.slice(0, 4).map((action) => <WorkItem key={action.id} action={action} />)}</ul>
        </WorkSection>
      )}

      {(role === "approver" || role === "admin") && (
        <WorkSection
          title="Ready For Decision"
          description="Policies waiting for final approval or rejection."
          count={approvalActions.length}
          empty={<SectionEmpty role={role} title="Approvals" />}
        >
          <ul className="space-y-2">{approvalActions.slice(0, 4).map((action) => <WorkItem key={action.id} action={action} />)}</ul>
        </WorkSection>
      )}

      {(role === "creator" || role === "admin") && (
        <WorkSection
          title="Drafts To Move Forward"
          description="Owned drafts that still need editing, reviewer assignment, or submission."
          count={draftActions.length}
          empty={<SectionEmpty role={role} title="Drafts" />}
        >
          <ul className="space-y-2">{draftActions.slice(0, 4).map((action) => <WorkItem key={action.id} action={action} />)}</ul>
        </WorkSection>
      )}

      {role !== "viewer" && (
        <WorkSection
          title="Comments To Resolve"
          description="Open comment threads on policies where you are a stakeholder."
          count={comments.length}
          empty={<SectionEmpty role={role} title="Comments" />}
        >
          <ul className="space-y-2">{comments.slice(0, 4).map((comment) => <CommentItem key={comment.id} comment={comment} />)}</ul>
        </WorkSection>
      )}

      <WorkSection
        title="Renewal Watch"
        description="Approved policies that are expired or coming up for renewal soon."
        count={renewalActions.length}
        empty={<SectionEmpty role={role} title="Renewals" />}
      >
        <ul className="space-y-2">{renewalActions.slice(0, 4).map((action) => <WorkItem key={action.id} action={action} />)}</ul>
      </WorkSection>
    </div>
  );
}

function InsightPanel({
  role,
  summary,
  actions,
  comments,
}: {
  role: UserRole;
  summary: DashboardSummary;
  actions: PendingAction[];
  comments: UnresolvedComment[];
}) {
  const insights = useMemo(() => {
    const items: Array<{ title: string; body: string; icon: typeof AlertCircle; tone: string }> = [];

    if (summary.renewalDue > 0) {
      items.push({
        title: "Renewal risk is visible",
        body: `${summary.renewalDue} approved polic${summary.renewalDue === 1 ? "y is" : "ies are"} expired or due within 30 days.`,
        icon: RefreshCw,
        tone: "text-orange-600 bg-orange-50 dark:bg-orange-950/20",
      });
    }

    if (summary.pendingApprovalPolicies > 0) {
      items.push({
        title: "Approval queue needs attention",
        body: `${summary.pendingApprovalPolicies} polic${summary.pendingApprovalPolicies === 1 ? "y is" : "ies are"} waiting for a decision.`,
        icon: CheckCircle,
        tone: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20",
      });
    }

    if (summary.underReviewPolicies > summary.pendingApprovalPolicies && summary.underReviewPolicies > 0) {
      items.push({
        title: "Review is the current bottleneck",
        body: `${summary.underReviewPolicies} polic${summary.underReviewPolicies === 1 ? "y is" : "ies are"} under review across the company.`,
        icon: Clock,
        tone: "text-blue-600 bg-blue-50 dark:bg-blue-950/20",
      });
    }

    if (comments.length > 0 && role !== "viewer") {
      items.push({
        title: "Open threads can slow workflow",
        body: `${comments.length} unresolved comment${comments.length === 1 ? "" : "s"} need stakeholder follow-up.`,
        icon: MessageSquare,
        tone: "text-violet-600 bg-violet-50 dark:bg-violet-950/20",
      });
    }

    if (actions.length === 0 && comments.length === 0 && summary.totalPolicies > 0) {
      items.push({
        title: "Your queue is clear",
        body: "No assigned policy actions are waiting on you right now.",
        icon: Sparkles,
        tone: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20",
      });
    }

    if (summary.totalPolicies === 0) {
      items.push({
        title: "Start with policy coverage",
        body: "Create or upload your first policy so the dashboard can track workflow health.",
        icon: FileText,
        tone: "text-blue-600 bg-blue-50 dark:bg-blue-950/20",
      });
    }

    if (items.length < 3) {
      items.push({
        title: "Policy health",
        body: `${summary.complianceRate}% of policies are approved across ${summary.totalPolicies} total polic${summary.totalPolicies === 1 ? "y" : "ies"}.`,
        icon: TrendingUp,
        tone: "text-muted-foreground bg-muted",
      });
    }

    return items.slice(0, 3);
  }, [actions.length, comments.length, role, summary]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-foreground">Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight) => {
          const Icon = insight.icon;
          return (
            <div key={insight.title} className="flex gap-3 rounded-lg border bg-card p-3">
              <div className={`h-8 w-8 shrink-0 rounded-md p-2 ${insight.tone}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{insight.title}</p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{insight.body}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

const Dashboard = () => {
  const { roles, profile } = useAuth();
  const { summary, loading: summaryLoading } = useDashboardSummary();
  const { actions, isLoading: actionsLoading } = usePendingActions();
  const { comments, isLoading: commentsLoading } = useUnresolvedComments();
  const role = getPrimaryRole(roles);
  const loadingWork = actionsLoading || commentsLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-medium tracking-tight text-foreground">Dashboard</h1>
            <Badge variant="secondary">{roleLabels[role]}</Badge>
          </div>
          <p className="max-w-3xl text-muted-foreground">
            {profile?.name ? `${profile.name}, ` : ""}
            {getRoleIntro(role)}
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {summary.teamMembers > 0 ? `${summary.teamMembers} team member${summary.teamMembers === 1 ? "" : "s"}` : "Team not loaded"}
        </div>
      </div>

      <RoleSummaryCards role={role} summary={summary} actions={actions} comments={comments} loading={summaryLoading || loadingWork} />
      <ProgramHealthCard />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Your Work</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Grouped by what needs a decision, follow-up, or visibility from your role.
            </p>
          </div>
          <YourWork role={role} actions={actions} comments={comments} loading={loadingWork} />
        </div>

        <aside className="space-y-5">
          <InsightPanel role={role} summary={summary} actions={actions} comments={comments} />
          <QuickActions />
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;
